import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Loader2,
  MessageCircle,
  MoreVertical,
  Phone,
  Search,
  Video,
  X,
} from "lucide-react";


import { useChat } from "../../features/chat/ChatProvider";
import { ChatConversation, ChatConversationCategory, ChatMessage } from "../../features/chat/types";
import { useAuthSession } from "../../lib/auth/session";
import MessageComposer from "./MessageComposer";

const CATEGORIES: Array<{ key: ChatConversationCategory; label: string }> = [
  { key: "inbox", label: "Inbox" },
  { key: "unread", label: "Unread" },
  { key: "communities", label: "Communities" },
];

const MINUTE = 60 * 1000;

function formatRelativeTime(value?: string | null) {
  if (!value) return "";
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) return "";
  const diff = Date.now() - timestamp;
  if (diff < MINUTE) return "Just now";
  if (diff < 60 * MINUTE) return `${Math.floor(diff / MINUTE)}m ago`;
  if (diff < 24 * 60 * MINUTE) return `${Math.floor(diff / (60 * MINUTE))}h ago`;
  return new Date(timestamp).toLocaleDateString();
}

function resolveConversationTitle(conversation: ChatConversation, currentUserId?: string | null) {
  if (conversation.title) return conversation.title;
  if (conversation.name) return conversation.name;
  const others = conversation.members.filter((member) => member.userId !== currentUserId);
  const displayNames = others.map((member) => member.displayName).filter(Boolean) as string[];
  if (displayNames.length === 0) return "Conversation";
  if (displayNames.length === 1) return displayNames[0];
  return displayNames.join(", ");
}

function sortMessages(messages: ChatMessage[]) {
  return [...messages].sort((a, b) => {
    const aTime = a.sentAt ? Date.parse(a.sentAt) : a.time ? Date.parse(a.time) : 0;
    const bTime = b.sentAt ? Date.parse(b.sentAt) : b.time ? Date.parse(b.time) : 0;
    return aTime - bTime;
  });
}

type FriendlyErrorCopy = {
  heading: string;
  description: string;
};

const DEFAULT_ERROR_COPY: FriendlyErrorCopy = {
  heading: "We couldn't load your messages",
  description: "Something went wrong while loading the chat. Please try again.",
};

const FRIENDLY_ERROR_MAPPINGS: Array<{
  test: (message: string) => boolean;
  copy: FriendlyErrorCopy;
}> = [
  {
    test: (message) =>
      message.includes("failed to fetch") || message.includes("network") || message.includes("connection"),
    copy: {
      heading: "Connection issue",
      description: "We couldn’t connect to the chat service. Check your network and try again.",
    },
  },
  {
    test: (message) => message.includes("timeout"),
    copy: {
      heading: "Chat is taking too long to respond",
      description: "The chat service timed out before responding. Please retry in a moment.",
    },
  },
  {
    test: (message) => message.includes("unauthorized") || message.includes("forbidden") || message.includes("401"),
    copy: {
      heading: "Your session needs attention",
      description: "Your chat session may have expired. Refresh to reconnect or sign in again if prompted.",
    },
  },
];

function resolveFriendlyErrorCopy(message: string | null): FriendlyErrorCopy {
  if (!message) return DEFAULT_ERROR_COPY;
  const normalized = message.toLowerCase();
  const match = FRIENDLY_ERROR_MAPPINGS.find((mapping) => mapping.test(normalized));
  return match?.copy ?? DEFAULT_ERROR_COPY;
}

const MessageBubble: React.FC<{
  message: ChatMessage;
  isOwn: boolean;
}> = ({ message, isOwn }) => {
  return (
    <div className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
      <div
         className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed shadow-sm ${
          isOwn
            ? "bg-[#1b74e4] text-white"
            : "bg-white text-slate-800 ring-1 ring-slate-200"
        }`}
      >
        <div className="whitespace-pre-wrap break-words">{message.text}</div>
        <div
          className={`mt-1 text-[10px] font-medium uppercase tracking-wider ${
            isOwn ? "text-blue-100" : "text-slate-400"
 
          }`}
        >
          {formatRelativeTime(message.time ?? message.sentAt)}
        </div>
      </div>
    </div>
  );
};

const ConversationListItem: React.FC<{
  conversation: ChatConversation;
  isActive: boolean;
  onSelect: (conversation: ChatConversation) => void;
  currentUserId?: string | null;
}> = ({ conversation, isActive, onSelect, currentUserId }) => {
  const unread = conversation.unreadCount ?? 0;
  const title = resolveConversationTitle(conversation, currentUserId);
  const preview = conversation.lastMessage ?? "No messages yet";
  const time = conversation.time ? formatRelativeTime(conversation.time) : "";

  const initials = useMemo(() => {
    const words = title.split(" ").filter(Boolean);
    if (words.length === 0) return "TM";
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return `${words[0][0] ?? ""}${words[1][0] ?? ""}`.toUpperCase();
  }, [title]);

  return (
    <button
      type="button"
      onClick={() => onSelect(conversation)}
      className={`group flex w-full items-start gap-3 rounded-2xl border border-transparent px-3 py-2 text-left transition ${
        isActive
          ? "bg-[#e7f3ff] text-[#1b74e4]"
          : "hover:bg-slate-100/80"
      }`}
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold shadow-inner ${
          isActive ? "bg-[#1b74e4] text-white" : "bg-slate-200 text-slate-600"
        }`}
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p
            className={`text-sm font-semibold line-clamp-1 ${
              isActive ? "text-[#1b74e4]" : "text-slate-900"
            }`}
          >
            {title}
          </p>
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
            {time}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <p className="flex-1 text-xs text-slate-500 line-clamp-2">{preview}</p>
          {unread > 0 && (
            <span className="inline-flex min-w-[1.5rem] justify-center rounded-full bg-[#1b74e4] px-2 py-0.5 text-[10px] font-semibold text-white">
              {unread}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

type FloatingChatWidgetProps = {
  variant?: "floating" | "navbar";
};

const FloatingChatWidget: React.FC<FloatingChatWidgetProps> = ({ variant = "floating" }) => {
  const { officerId, displayName } = useAuthSession();
  const {
    snapshot,
    activeConversation,
    activeConversationId,
    setActiveConversation,
    loadConversation,
    sendMessage,
    markConversationAsRead,
    loadingSnapshot,
    error,
    refreshSnapshot,
  } = useChat();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ChatConversationCategory>("inbox");
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const messageListRef = useRef<HTMLDivElement | null>(null);

  const conversations = snapshot.conversations[selectedCategory] ?? [];

  const friendlyError = useMemo(() => resolveFriendlyErrorCopy(error), [error]);

  const filteredConversations = useMemo(() => {
    if (!searchTerm.trim()) return conversations;
    const normalized = searchTerm.trim().toLowerCase();
    return conversations.filter((conversation) => {
      const title = resolveConversationTitle(conversation, officerId) || "";
      const lastMessage = conversation.lastMessage || "";
      return (
        title.toLowerCase().includes(normalized) ||
        lastMessage.toLowerCase().includes(normalized)
      );
    });
  }, [conversations, officerId, searchTerm]);

  const sortedMessages = useMemo(() => {
    if (!activeConversation) return [];
    return sortMessages(activeConversation.messages ?? []);
  }, [activeConversation]);

  const activeConversationTitle = useMemo(() => {
    if (!activeConversation) return "No conversation selected";
    return resolveConversationTitle(activeConversation, officerId) || "Telemedicine Chat";
  }, [activeConversation, officerId]);

  const activeConversationSubtitle = useMemo(() => {
    if (!activeConversation) return "Choose a chat from the list to view messages.";
    const others = activeConversation.members?.filter((member) => member.userId !== officerId) ?? [];
    const names = others.map((member) => member.displayName).filter(Boolean) as string[];
    if (names.length === 0) return "Direct conversation";
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    return `${names[0]}, ${names[1]} + ${names.length - 2} more`;
  }, [activeConversation, officerId]);

  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [sortedMessages, isOpen]);

  useEffect(() => {
    if (!activeConversation) return;
    if ((activeConversation.unreadCount ?? 0) > 0) {
      markConversationAsRead(activeConversation.id);
    }
  }, [activeConversation, markConversationAsRead]);

  useEffect(() => {
    setSendError(null);
  }, [activeConversationId]);

  const handleToggle = useCallback(() => {
    setIsOpen((value) => !value);
    if (!isOpen && !activeConversationId) {
      refreshSnapshot().catch(() => undefined);
    }
  }, [isOpen, activeConversationId, refreshSnapshot]);

  const handleSelectConversation = useCallback(
    async (conversation: ChatConversation) => {
      setActiveConversation(conversation.id);
      await loadConversation(conversation.id, { size: 50 });
    },
    [loadConversation, setActiveConversation]
  );

  const handleComposerSubmit = useCallback(
    async (text: string) => {
      if (!activeConversationId) {
        setSendError("Select a conversation to send a message.");
        return false;
      }

      setSendError(null);
      setIsSending(true);
      try {
        await sendMessage({
          conversationId: activeConversationId,
          text,
          senderId: officerId,
          senderName: displayName,
        });
        return true;
      } catch (err) {
        setSendError(err instanceof Error ? err.message : "Failed to send message");
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [activeConversationId, displayName, officerId, sendMessage]
  );

  const triggerButton = (
    <button
      type="button"
      onClick={handleToggle}
      aria-expanded={isOpen}
      aria-label={isOpen ? "Close chat" : "Open chat"}
      className={`${
        variant === "navbar"
          ? "inline-flex h-10 w-10 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-indigo-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-200"
          : "inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-xl ring-2 ring-white transition-transform hover:scale-105 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300"
      } ${isOpen && variant === "navbar" ? "ring-2 ring-indigo-200" : ""}`}
    >
      <MessageCircle size={variant === "navbar" ? 18 : 22} />
    </button>
  );

  const chatPanel = (
    <div className="flex h-auto max-h-[80vh] flex-col overflow-hidden rounded-3xl border border-[#dfe2e7] bg-white text-slate-900 shadow-[0_18px_44px_rgba(15,20,40,0.16)] sm:h-[560px]">
      <header className="flex items-center justify-between border-b border-[#e4e6eb] px-5 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1b74e4]/10 text-[#1b74e4]">
            <MessageCircle size={18} />
          </div>
          <span className="text-sm font-semibold text-slate-900">Telemedicine Chat</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            Signed in as {displayName || officerId || "you"}
          </span>
          <button
            type="button"
            onClick={handleToggle}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-200"
            aria-label="Close chat"
          >
            <X size={16} />
          </button>
        </div>
      </header>
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <aside className="flex w-44 min-h-0 flex-col border-r border-[#f1f5f9] bg-[#f8fafc]">
          <div className="px-4 pb-3 pt-4">
            <div className="flex gap-2">
              {CATEGORIES.map((category) => (
                <button
                  key={category.key}
                  type="button"
                  onClick={() => {
                    setSelectedCategory(category.key);
                    setSearchTerm("");
                  }}
                  className={`flex-1 rounded-full px-3 py-1.5 text-[11px] font-semibold transition ${
                    selectedCategory === category.key
                      ? "bg-[#e7f3ff] text-[#1b74e4]"
                      : "bg-[#edf2f7] text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
            <div className="mt-3">
              <label className="sr-only" htmlFor="chat-search">
                Search conversations
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  id="chat-search"
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="w-full rounded-full border border-transparent bg-white py-2 pl-9 pr-3 text-xs text-slate-600 shadow-inner focus:border-[#1b74e4] focus:outline-none focus:ring-2 focus:ring-[#bfdbfe]"
                  placeholder="Search"
                />
              </div>
            </div>
          </div>
          <div className="flex-1 space-y-2 overflow-y-auto px-4 pb-4 min-h-0">
            {loadingSnapshot && conversations.length === 0 ? (
              <div className="flex h-full items-center justify-center text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading...
              </div>
            ) : filteredConversations.length === 0 ? (
              <p className="text-xs text-slate-500">No conversations found</p>
            ) : (
              filteredConversations.map((conversation) => (
                <ConversationListItem
                  key={conversation.id}
                  conversation={conversation}
                  isActive={conversation.id === activeConversationId}
                  onSelect={handleSelectConversation}
                  currentUserId={officerId}
                />
              ))
            )}
          </div>
        </aside>
        <section className="flex min-h-0 flex-1 flex-col bg-white">
          <div className="flex items-center justify-between border-b border-[#f1f5f9] px-6 py-4">
            <div>
              <p className="text-sm font-semibold text-slate-900">{activeConversationTitle}</p>
              <p className="text-xs text-slate-500">{activeConversationSubtitle}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#ecfdf5] text-[#047857] transition hover:bg-[#d1fae5]"
                aria-label="Start audio call"
              >
                <Phone size={16} />
              </button>
              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#eef2ff] text-[#4338ca] transition hover:bg-[#e0e7ff]"
                aria-label="Start video call"
              >
                <Video size={16} />
              </button>
              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#f1f5f9] text-slate-500 transition hover:bg-[#e2e8f0]"
                aria-label="More options"
              >
                <MoreVertical size={16} />
              </button>
            </div>
          </div>
          {error && (
            <div className="border-b border-rose-100 bg-rose-50/90 px-5 py-3 text-sm text-rose-900">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-rose-500" aria-hidden="true" />
                <div className="flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-rose-700">{friendlyError.heading}</p>
                      <p className="mt-1 text-xs text-rose-600">{friendlyError.description}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        refreshSnapshot().catch(() => undefined);
                      }}
                      className="inline-flex items-center justify-center rounded-full border border-rose-200 bg-white px-3 py-1 text-xs font-semibold text-rose-700 shadow-sm transition hover:border-rose-300 hover:bg-rose-100"
                    >
                      Retry
                    </button>
                  </div>
                  <details className="mt-2 text-xs text-rose-600">
                    <summary className="cursor-pointer text-xs font-medium text-rose-600 hover:text-rose-500">
                      Technical details
                    </summary>
                    <pre className="mt-1 max-h-32 overflow-auto rounded bg-rose-100/70 p-2 text-[11px] leading-relaxed text-rose-700">{error}</pre>
                  </details>
                </div>
              </div>
            </div>
          )}
          <div ref={messageListRef} className="flex-1 space-y-3 overflow-y-auto bg-[#f8fafc] px-6 py-4 min-h-0">
            {activeConversation ? (
              sortedMessages.length > 0 ? (
                sortedMessages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isOwn={Boolean(officerId && message.senderId === officerId)}
                  />
                ))
              ) : (
                <p className="text-center text-xs text-slate-500">No messages yet. Say hello!</p>
              )
            ) : (
              <div className="flex h-full items-center justify-center text-center text-xs text-slate-500">
                Select a conversation to view messages.
              </div>
            )}
          </div>
          <MessageComposer
            onSubmit={handleComposerSubmit}
            loading={isSending}
            disabled={!activeConversation}
            error={sendError}
            placeholder={
              activeConversation
                ? "Type a message to your team…"
                : "Select a conversation to start messaging."
            }
          />
        </section>
      </div>
    </div>
  );

  if (variant === "navbar") {
    return (
      <div className="relative">
        {triggerButton}
        {isOpen && (
          <div className="absolute right-0 mt-3 w-[min(90vw,22rem)] max-w-sm">
            <div className="pointer-events-auto">
              {chatPanel}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`pointer-events-auto ${isOpen ? "w-full max-w-sm sm:w-[380px]" : "w-14"}`}>
      {!isOpen ? triggerButton : chatPanel}
    </div>
  );
};

export default FloatingChatWidget;
